import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Rule as RuleIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { Document as DocumentType, Permit } from '../../types';

interface ProjectComplianceChecksProps {
  permits: Permit[];
  documentsWithReports: Set<string>;
  isAnalyzingDocument: string | null;
  handleRunDocumentCompliance: (document: DocumentType, permitId: string) => void;
  handleViewComplianceReport: (documentId: string) => void;
}

const ProjectComplianceChecks: React.FC<ProjectComplianceChecksProps> = ({
  permits,
  documentsWithReports,
  isAnalyzingDocument,
  handleRunDocumentCompliance,
  handleViewComplianceReport,
}) => {
  const theme = useTheme();

  return (
    <Paper sx={{ p: 3, borderRadius: '16px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Compliance Checks</Typography>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Document Name</TableCell>
              <TableCell>Permit Type</TableCell>
              <TableCell align="center">Run Check</TableCell>
              <TableCell align="center">Report</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {permits.flatMap(permit =>
              (permit.documents || []).map(document => (
                <TableRow
                  key={document.documentID}
                  hover
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {document.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {permit.permitType}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRunDocumentCompliance(document, permit.permitID);
                      }}
                      disabled={isAnalyzingDocument === document.documentID}
                      sx={{ minWidth: 'auto', p: 1 }}
                    >
                      {isAnalyzingDocument === document.documentID ? (
                        <CircularProgress size={20} />
                      ) : (
                        <RuleIcon />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    {documentsWithReports.has(document.documentID) ? (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewComplianceReport(document.documentID);
                        }}
                        sx={{
                          bgcolor: theme.palette.success.main,
                          '&:hover': {
                            bgcolor: theme.palette.success.dark,
                          },
                          minWidth: 'auto',
                          p: 1,
                        }}
                      >
                        <VisibilityIcon />
                      </Button>
                    ) : (
                      <Box sx={{ height: 32 }} /> // Placeholder for alignment
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ProjectComplianceChecks; 